import React, { useRef, useState, useCallback } from 'react';
import PullToRefresh from 'react-simple-pull-to-refresh';

/**
 * Contexts
 */
import { useContext } from 'react';
import { AppContext } from '../../contexts/AppContext';

/**
 * Hooks
 */
import { useAppMenu } from '../../contexts/NavBarContext';
import { useTranslation } from 'react-i18next';
import { useNetworkState } from '@uidotdev/usehooks';
import useVehicleAlerts from '../../hooks/Vehicles/useVehicleWorkshopAlerts';

/**
 * Utils
 */
import { sendWorkshopAlert } from '../../utils/api';

/**
 * Components
 */
import ListVehicleAlerts from '../../components/Vehicles/ListVehicleAlerts';
import SendVehicleAlertForm from '../../components/Vehicles/SendVehicleAlertForm';

/**
 * MUI Components
 */
import { Box, CircularProgress } from '@mui/material';
import Fab from '@mui/material/Fab';
import Dialog from '@mui/material/Dialog';
import { Snackbar, Alert } from '@mui/material';
import MinorCrashIcon from '@mui/icons-material/MinorCrash';

const VehicleAlerts = () => {
    const { t } = useTranslation();
    const appData = useContext(AppContext);
    const networkState = useNetworkState();
    const [openForm, setOpenForm] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [toastOpen, setToastOpen] = useState(false);
    const { vehicleWorkshopAlerts, loading, error, loadMore, hasMore, forceRefresh } = useVehicleAlerts(appData.token);
    const { updateTitle } = useAppMenu();

    // Change the main menu title
    updateTitle(t('Vehicles Alerts'));

    // Load more notifications when the user scrolls to the bottom of the list
    const observer = useRef();
    const lastItemRef = useCallback((node) => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                loadMore();
            }
        });

        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    // Vehicle alert dialog/form
    const handleOpenForm = () => setOpenForm(true);
    const handleCloseForm = () => setOpenForm(false);
    const handleSendVehicleAlert = (data) => {
        if (isSending) return;

        setIsSending(true);
        sendWorkshopAlert(appData.token, data).then(() => {
            setIsSending(false);
            setOpenForm(false);
            setToastOpen(true);

            forceRefresh(); // Reload/Refresh the list of alerts
        }).catch((error) => {
            console.error('Error sending notification', error);
            setIsSending(false);
        });
    };

    return (
        <>
            <Dialog open={openForm} onClose={handleCloseForm} fullWidth maxWidth="sm">
                <SendVehicleAlertForm onClose={handleCloseForm} onSubmit={handleSendVehicleAlert} />
            </Dialog>
            <Box sx={{ p: 2, marginBottom: '100px' }}>
                <PullToRefresh onRefresh={forceRefresh}>
                    <ListVehicleAlerts items={vehicleWorkshopAlerts} lastItemRef={lastItemRef} />
                </PullToRefresh>
                {loading && (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <CircularProgress />
                    </Box>
                )}
                {!loading && !hasMore && vehicleWorkshopAlerts.length > 0 && (
                    <Box sx={{ textAlign: 'center', mt: 2, color: 'text.secondary' }}>
                        {t('No more vehicle alerts')}
                    </Box>
                )}
                {error && <div style={{ textAlign: 'center', color: 'red' }}>{t('error')}: {error.message}</div>}

                <Fab color="primary"
                    disabled={!networkState.online}
                    aria-label="send vehicle alert"
                    onClick={handleOpenForm}
                    sx={{ position: 'fixed', bottom: 80, right: 20 }}>
                    <MinorCrashIcon />
                </Fab>
            </Box>
            <Snackbar
                open={toastOpen}
                autoHideDuration={3000}
                onClose={() => setToastOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={() => setToastOpen(false)} severity="success" sx={{ width: '100%' }}>
                    {t('Alert sent successfully')}
                </Alert>
            </Snackbar>
        </>
    );
};

export default VehicleAlerts;